/* ===========================================================
   HOME PAGE — Hero, Categories, Featured, About, Stats, Trust,
   Certificates, FAQ, CTA banner
   =========================================================== */
import React from "react";
import { ProductCard } from "@/components/product-card";
import { NETHRA_DATA } from "@/data/data";
import { Icons } from "@/lib/icons";
import { IMG, CATEGORY_IMAGES } from "@/lib/images";
import { useProducts } from "@/api/hooks";

const { useState: hUS, useEffect: hUE, useRef: hUR } = React;

/* ---------- HERO ---------- */
const HERO_IMAGE = "https://yuvrit.in/cdn/shop/files/Firefly_A_premium_photorealistic_Ayurvedic_wellness_composition_showing_a_therapist_s_hands_921634_1_600x.jpg?v=1756965800";

function HomeHero() {
  const slides = NETHRA_DATA.heroSlides;
  const [idx, setIdx] = hUS(0);
  const total = slides.length;
  const cur = slides[idx];
  const images = [HERO_IMAGE, HERO_IMAGE, HERO_IMAGE];

  hUE(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 8000);
    return () => clearInterval(t);
  }, [total]);

  return (
    <section className="hero">
      <div className="container hero-grid">
        <div className="hero-left">
          <React.Fragment key={"slide-" + idx}>
          <div className="hero-tag">
            <span className="dot" />
            <span className="kicker">{cur.eyebrow}</span>
            <span className="hero-tag-sep" />
            <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{cur.tag}</span>
          </div>
          <h1 className="hero-headline">{cur.headline}</h1>
          <p className="hero-sub">{cur.subheadline}</p>

          <div className="hero-cta-row">
            <a className="btn btn-primary btn-lg" href={cur.cta_url}>
              {cur.cta_text} <Icons.ArrowR size={16} stroke={2} />
            </a>
            <a className="btn btn-outline btn-lg" href="#">Talk to procurement</a>
          </div>

          <div className="hero-meta-rail">
            {cur.meta.map((m, i) => (
              <div key={i} className="hero-meta-item">
                <Icons.Check size={13} stroke={2.2} />
                <span>{m}</span>
              </div>
            ))}
          </div>
          </React.Fragment>

          <div className="hero-slide-control">
            <div className="hero-counter mono">
              <span className="hero-counter-now">{String(idx + 1).padStart(2, "0")}</span>
              <span className="hero-counter-sep">/</span>
              <span>{String(total).padStart(2, "0")}</span>
            </div>
            <div className="hero-dots">
              {slides.map((_, i) => (
                <button key={i}
                        className={"hero-dot" + (i === idx ? " is-active" : "")}
                        onClick={() => setIdx(i)}
                        aria-label={`Slide ${i + 1}`} />
              ))}
            </div>
            <div className="hero-arrows">
              <button className="icon-btn hero-arrow" onClick={() => setIdx((idx - 1 + total) % total)} aria-label="Previous"><Icons.ChevL size={16} /></button>
              <button className="icon-btn hero-arrow" onClick={() => setIdx((idx + 1) % total)} aria-label="Next"><Icons.ChevR size={16} /></button>
            </div>
          </div>
        </div>

        <div className="hero-right">
          <div className="hero-card">
            <div className="hero-card-image">
              <img src={images[idx]} alt="Pharmacy fulfillment" />
              <div className="hero-card-image-tag">{cur.tag} · CAMPAIGN VISUAL</div>
            </div>
            <div className="hero-card-body">
              <div className="hero-card-row">
                <div>
                  <div className="kicker" style={{ fontSize: 10 }}>WAREHOUSE</div>
                  <div className="hero-card-strong">Palayamkottai · Tirunelveli</div>
                </div>
                <span className="pill pill-emerald"><span className="dot" /> Operational</span>
              </div>
              <div className="hero-metric-row">
                <HeroMetric k="ACTIVE SKUS"  v="12,400" sub="+184 this week" />
                <HeroMetric k="DISPATCH SLA" v="99.4%"  sub="rolling 30d" />
                <HeroMetric k="LANES"        v="50"     sub="cities · pan-india" />
              </div>
              <div>
                <div className="hero-temp">
                  <Icons.Snowflake size={14} stroke={1.8} />
                  <span className="mono" style={{ fontSize: 11 }}>COLD CHAIN</span>
                  <span className="hero-temp-val">4.2°C</span>
                  <span className="hero-temp-bar"><span className="hero-temp-fill" style={{ width: "42%" }} /></span>
                  <span className="mono" style={{ fontSize: 10, color: "var(--ink-3)" }}>2–8°C</span>
                </div>
              </div>
            </div>
          </div>
          <div className="hero-float">
            <div className="hero-float-icon"><Icons.ShieldCheck size={20} stroke={1.8} /></div>
            <div>
              <div className="hero-float-num">WHO-GDP</div>
              <div className="hero-float-label">Audited · 2026 cycle</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
function HeroMetric({ k, v, sub }) {
  return (
    <div className="hero-metric">
      <div className="hero-metric-k">{k}</div>
      <div className="hero-metric-v">{v}</div>
      {sub && <div className="hero-metric-sub">{sub}</div>}
    </div>
  );
}

/* ---------- CATEGORIES ---------- */
function HomeCategories() {
  const cats = NETHRA_DATA.categories;
  const catImg = CATEGORY_IMAGES;
  return (
    <section className="section" data-screen-label="Categories">
      <div className="container">
        <div className="section-head reveal">
          <div>
            <div className="section-eyebrow">
              <span className="bar" />
              <Icons.Grid size={14} stroke={1.8} />
              <span className="kicker">Catalog · 8 categories</span>
            </div>
            <h2 className="section-title">Every line in the Indian formulary, organized.</h2>
            <p className="section-desc">
              From schedule-H prescription stock to cold-chain biologics — browse by therapeutic area, regulatory class, or fulfillment lane.
            </p>
          </div>
          <div className="right">
            <a href="/pages/storefront/categories.html" className="btn btn-outline">
              View all categories <Icons.ArrowR size={14} stroke={2} />
            </a>
          </div>
        </div>

        <div className="cat-grid">
          {cats.map((c, i) => {
            return (
              <a key={c.id} href={"/pages/storefront/category.html?slug=" + c.slug} className="cat-card has-image reveal" data-delay={Math.min(i, 5)}>
                <div className="cat-card-media">
                  <img src={catImg[c.slug]} alt={c.name} loading="lazy" />
                </div>
                <div className="cat-card-body">
                  <div className="cat-card-name">{c.name}</div>
                  <div className="cat-card-desc">{c.description}</div>
                </div>
                <div className="cat-card-foot">
                  <span className="mono cat-card-count">
                    {c.product_count.toLocaleString("en-IN")} <span style={{ color: "var(--ink-3)" }}>SKUs</span>
                  </span>
                  <span className="cat-card-arrow"><Icons.ArrowUR size={14} stroke={1.8} /></span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- FEATURED PRODUCTS ---------- */
function HomeFeatured() {
  // Live catalogue from the API. Falls back to the static mock list when the
  // backend isn't reachable so design review keeps working offline.
  const { data, isLoading, isError } = useProducts({ featured: true, limit: 8 });

  const apiProducts = data?.items ?? [];
  const fallback = NETHRA_DATA.products.filter((p) => p.is_featured).slice(0, 8);
  const products = isError || (!isLoading && apiProducts.length === 0)
    ? fallback
    : apiProducts;
  const usingApi = !isError && apiProducts.length > 0;

  return (
    <section className="section featured" data-screen-label="Featured products">
      <div className="container">
        <div className="section-head reveal">
          <div>
            <div className="section-eyebrow">
              <span className="bar" />
              <Icons.Sparkle size={14} stroke={1.8} />
              <span className="kicker">Featured · curated weekly</span>
            </div>
            <h2 className="section-title">High-velocity stock.</h2>
            <p className="section-desc">
              Ten lines this team thinks every pharmacy floor should be carrying right now.
            </p>
          </div>
          <div className="right" style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-end" }}>
            <a href="/pages/storefront/products.html" className="btn btn-outline btn-sm">
              View all 12,400 SKUs <Icons.ArrowR size={14} stroke={2} />
            </a>
          </div>
        </div>

        <div className="meta-rail reveal">
          <span className="meta-dot" />
          <span className="mono" style={{ color: "var(--ink-2)" }}>
            {isLoading ? "Loading…" : `${products.length} products shown`}
          </span>
          <span className="meta-sep" />
          <span className="kicker">{usingApi ? "Live catalog · API" : "Live catalog"}</span>
        </div>

        <div className="prod-grid cols-4 reveal">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </section>
  );
}

/* ---------- ABOUT (split image + stats + trust) ---------- */
function AnimatedCounter({ to, suffix = "", decimal = false }) {
  const [n, setN] = hUS(0);
  const ref = hUR(null);
  hUE(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const start = performance.now();
          const dur = 1400;
          const tick = (t) => {
            const p = Math.min(1, (t - start) / dur);
            const e = 1 - Math.pow(1 - p, 3);
            setN(to * e);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          obs.disconnect();
        }
      }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  const display = decimal ? n.toFixed(1) : Math.floor(n).toLocaleString("en-IN");
  return <span ref={ref} className="mono">{display}<span className="counter-suffix">{suffix}</span></span>;
}

function HomeAbout() {
  const bullets = [
    "Direct from licensed manufacturers — no triple-handling",
    "Cold-chain validated lanes for biologics & vaccines",
    "Transparent role-based pricing, no hidden trade margins",
    "Single PO across 8 categories, one statement, one invoice",
  ];
  const stats = NETHRA_DATA.stats;
  const badges = NETHRA_DATA.trustBadges;
  return (
    <section className="section about" id="about" data-screen-label="About">
      <div className="container">
        <div className="about-grid">
          <div className="about-visual reveal">
            <span className="about-bracket about-bracket-tl" />
            <span className="about-bracket about-bracket-br" />
            <div className="about-image-card">
              <img src="/company.jpeg" alt="Nethrasap team — Tirunelveli office" />
              <div className="about-image-overlay" />
              <div className="about-year">
                <div className="about-year-ic"><Icons.Award size={16} stroke={1.8} /></div>
                <div>
                  <div className="kicker" style={{ fontSize: 10, color: "rgba(255,255,255,0.7)" }}>SINCE 2016</div>
                  <div className="about-year-label">Trusted partner to 5,200+ buyers</div>
                </div>
              </div>
            </div>
            <div className="about-float">
              <div className="about-float-ic"><Icons.ShieldCheck size={18} stroke={1.8} /></div>
              <div>
                <div className="about-float-num">100%</div>
                <div className="about-float-label">Authentic, traceable</div>
              </div>
            </div>
          </div>

          <div className="about-content reveal" data-delay="1">
            <div className="section-eyebrow">
              <span className="bar" />
              <Icons.Building size={14} stroke={1.8} />
              <span className="kicker">About nethrasap</span>
            </div>
            <h2 className="section-title" style={{ fontSize: "clamp(28px,3.6vw,40px)" }}>
              We move medicine through India — without the friction.
            </h2>
            <p className="about-desc">
              Nethrasap is a healthcare supply platform built for the operating reality of Indian pharmacies, clinics and hospital procurement teams. We replace seven layers of distributor markup with a single, audited supply line.
            </p>
            <ul className="about-bullets">
              {bullets.map((b, i) => (
                <li key={i} className="about-bullet">
                  <span className="about-bullet-ic"><Icons.Check size={12} stroke={2.6} /></span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="about-cta-row">
              <a href="/pages/storefront/products.html" className="btn btn-primary">Explore the catalog <Icons.ArrowR size={14} stroke={2} /></a>
              <a href="#compliance" className="btn btn-ghost">Read our compliance brief <Icons.ArrowUR size={14} stroke={2} /></a>
            </div>
          </div>
        </div>

        <div className="stats-strip reveal">
          <div className="stats-strip-bar" />
          {stats.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Sparkle;
            return (
              <div key={i} className="stat-cell">
                <div className="stat-ic"><Icon size={16} stroke={1.8} /></div>
                <div className="stat-val">
                  <AnimatedCounter to={s.value} suffix={s.suffix} decimal={s.decimal} />
                </div>
                <div className="stat-label">{s.label}</div>
              </div>
            );
          })}
        </div>

        <div className="trust-grid" style={{ marginTop: 18 }}>
          {badges.map((b, i) => {
            const Icon = Icons[b.icon] || Icons.Sparkle;
            return (
              <div key={i} className="trust-card reveal" data-delay={i}>
                <div className="trust-ic"><Icon size={20} stroke={1.7} /></div>
                <div>
                  <div className="trust-label">{b.label}</div>
                  <div className="trust-desc">{b.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- CERTIFICATES ---------- */
function HomeCertificates() {
  const certs = NETHRA_DATA.certificates;
  const [active, setActive] = hUS(null);
  return (
    <section className="section certs" id="compliance" data-screen-label="Compliance">
      <div className="container">
        <div className="section-head section-head-center reveal">
          <div style={{ textAlign: "center", width: "100%" }}>
            <div className="section-eyebrow" style={{ justifyContent: "center" }}>
              <span className="bar" />
              <Icons.ShieldCheck size={14} stroke={1.8} />
              <span className="kicker">Compliance · 6 active credentials</span>
              <span className="bar" />
            </div>
            <h2 className="section-title" style={{ margin: "0 auto" }}>Audited end-to-end. Every shipment, every lane.</h2>
            <p className="section-desc" style={{ margin: "12px auto 0" }}>
              We carry every license and certification a wholesale healthcare distributor in India needs to operate — and a few that aren't required, but we hold anyway.
            </p>
          </div>
        </div>

        <div className="cert-grid">
          {certs.map((c, i) => {
            const statusMeta = {
              active:   { cls: "pill-emerald", label: "Active" },
              expiring: { cls: "pill-amber",   label: "Expiring soon" },
              expired:  { cls: "pill-rose",    label: "Expired" },
              lifetime: { cls: "pill-brand",   label: "Lifetime" },
            }[c.status] || { cls: "pill", label: c.status };
            return (
              <button key={c.id} className="cert-card reveal" data-delay={Math.min(i, 5)} onClick={() => setActive(c)}>
                <div className="cert-card-top">
                  <div className="cert-card-logo">{c.issuer.split(" ").slice(0,2).map((w) => w[0]).join("").toUpperCase()}</div>
                  <span className={"pill " + statusMeta.cls}>{statusMeta.label}</span>
                </div>
                <div className="cert-card-body">
                  <span className="kicker">{c.category}</span>
                  <h3 className="cert-card-name">{c.name}</h3>
                  <div className="cert-card-issuer">
                    <Icons.Building size={12} stroke={1.8} /> {c.issuer}
                  </div>
                </div>
                <div className="cert-card-foot mono">
                  <span><Icons.Hash size={11} stroke={1.8} /> {c.credential_id}</span>
                  <Icons.ArrowUR size={14} stroke={1.8} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {active && (
        <div className="modal-backdrop" onClick={() => setActive(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="icon-btn modal-close" onClick={() => setActive(null)} aria-label="Close"><Icons.X size={16} /></button>
            <div className="modal-header">
              <div className="cert-card-logo" style={{ width: 64, height: 64 }}>
                {active.issuer.split(" ").slice(0,2).map((w) => w[0]).join("").toUpperCase()}
              </div>
              <div>
                <span className="pill pill-brand">{active.category}</span>
                <h3 style={{ fontSize: 22, marginTop: 10 }}>{active.name}</h3>
                <div style={{ color: "var(--ink-3)", fontSize: 14, marginTop: 4 }}>{active.issuer}</div>
              </div>
            </div>
            <p className="modal-desc">
              Issued under {active.issuer}'s formal certification program. This credential confirms nethrasap's compliance with all applicable handling, storage and distribution requirements as of the issuance date, and is subject to annual surveillance audits.
            </p>
            <dl className="modal-dl">
              <div><dt>Credential ID</dt><dd className="mono">{active.credential_id}</dd></div>
              <div><dt>Issued</dt><dd>{active.issued_date}</dd></div>
              <div><dt>Expires</dt><dd>{active.expiry_date || "—"}</dd></div>
              <div><dt>Issuer</dt><dd>{active.issuer}</dd></div>
            </dl>
            <div className="modal-actions">
              <button className="btn btn-primary"><Icons.Download size={14} stroke={2} /> Download PDF</button>
              <button className="btn btn-outline">Verify online <Icons.ExtLink size={13} stroke={2} /></button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- FAQ ---------- */
function HomeFAQ() {
  const faqs = NETHRA_DATA.faqs;
  const [open, setOpen] = hUS(0);
  return (
    <section className="section faq" id="faq" data-screen-label="FAQ">
      <div className="container faq-container">
        <div className="faq-grid">
          <div className="faq-left reveal">
            <div className="section-eyebrow">
              <span className="bar" />
              <Icons.Help size={14} stroke={1.8} />
              <span className="kicker">FAQ · the four we're asked most</span>
            </div>
            <h2 className="section-title">Questions buyers actually ask before signing up.</h2>
            <p className="section-desc">
              Can't find your answer here? Our procurement team replies on email within 4 business hours — usually faster.
            </p>
            <div className="faq-contact-card">
              <div className="faq-contact-row">
                <Icons.Mail size={14} stroke={1.8} />
                <span className="mono" style={{ fontSize: 13 }}>procurement@nethrasap.in</span>
              </div>
              <div className="faq-contact-row">
                <Icons.Phone size={14} stroke={1.8} />
                <span className="mono" style={{ fontSize: 13 }}>+91 1800-419-7700</span>
              </div>
            </div>
          </div>
          <div className="faq-right reveal" data-delay="1">
            {faqs.map((f, i) => (
              <div key={f.id} className={"faq-item" + (open === i ? " is-open" : "")}>
                <button className="faq-trigger" onClick={() => setOpen(open === i ? -1 : i)}>
                  <span className="faq-num mono">0{i + 1}</span>
                  <span className="faq-q">{f.question}</span>
                  <span className="faq-chev"><Icons.Plus size={16} stroke={1.8} /></span>
                </button>
                <div className="faq-content">
                  <div className="faq-content-inner">{f.answer}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- CTA banner ---------- */
function HomeCTA() {
  return (
    <section className="cta-banner-section">
      <div className="container">
        <div className="cta-banner reveal">
          <div className="cta-banner-bg" />
          <div className="cta-banner-content">
            <span className="kicker" style={{ color: "var(--cream)", opacity: 0.85 }}>Open an account · free</span>
            <h2 className="cta-banner-title">Get your verified buyer ledger in under 6 minutes.</h2>
            <p className="cta-banner-sub">
              Upload your CDSCO, IMC or GST credentials. We'll match you to the right pricing tier and surface a starter assortment for your floor.
            </p>
            <div className="cta-banner-row">
              <a href="#" className="btn btn-lg" style={{ background: "#fff", color: "var(--ink)" }}>
                Create an account <Icons.ArrowR size={15} stroke={2} />
              </a>
              <a href="#" className="btn btn-lg btn-ghost" style={{ color: "var(--paper)", border: "1px solid rgba(255,255,255,0.18)" }}>
                Book a 15-min walk-through
              </a>
            </div>
          </div>
          <div className="cta-banner-stats">
            <div><div className="cta-stat-v mono">5,200+</div><div className="cta-stat-k">verified buyers</div></div>
            <div><div className="cta-stat-v mono">4 hr</div><div className="cta-stat-k">avg verification</div></div>
            <div><div className="cta-stat-v mono">₹0</div><div className="cta-stat-k">platform fee</div></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <HomeHero />
      <HomeCategories />
      <HomeFeatured />
      <HomeAbout />
      <HomeCertificates />
      <HomeFAQ />
      <HomeCTA />
    </>
  );
}

export { HomePage, HomeHero, HomeCategories, HomeFeatured, HomeAbout, HomeCertificates, HomeFAQ, HomeCTA };

